import { integrationApi } from "@repo/api";
import { useQuery } from "@tanstack/react-query";
import { ALLOWED_INTEGRATION_TYPES } from "../constants/integrationTypes";
import { useRouting } from "../contexts/RoutingContext";

export interface IntegrationTypeOption {
  id: number;
  typeName: string;
  displayName: string;
  description: string;
  iconUrl: string;
  isEnabled: boolean;
  isPrivateApp: boolean;
  encryptionConfig: string;
  value: string;
  label: string;
  isChecked: boolean;
}

export type IntegrationTypeCategory = "application" | "identitySources";

interface UseIntegrationTypesOptions {
  enabled?: boolean;
  category?: IntegrationTypeCategory;
}

function buildStandaloneMock(): IntegrationTypeOption[] {
  return ALLOWED_INTEGRATION_TYPES.map((typeName) => ({
    id: 0,
    typeName,
    displayName: typeName,
    description: "",
    iconUrl: "",
    isEnabled: true,
    isPrivateApp: false,
    encryptionConfig: "",
    value: typeName.toLowerCase(),
    label: typeName,
    isChecked: false,
  }));
}

export function useIntegrationTypes(options?: UseIntegrationTypesOptions) {
  let isStandaloneMode = false;
  try {
    const routing = useRouting();
    isStandaloneMode = routing?.isStandaloneMode ?? false;
  } catch (error) {
    console.warn("RoutingContext not available, defaulting to MFE mode", error);
    isStandaloneMode = false;
  }
  const category = options?.category;

  return useQuery({
    queryKey: ["integrationTypes", isStandaloneMode, category ?? null],
    queryFn: async (): Promise<IntegrationTypeOption[]> => {
      if (isStandaloneMode) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return buildStandaloneMock();
      }

      const types = await integrationApi.getIntegrationTypes(category);
      return types.map((type) => ({
        ...type,
        isChecked: false,
      }));
    },
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
    retry: isStandaloneMode ? false : 1,
  });
}
